import { queryClient } from "@api/queryClient";
import { useSessionStore } from "@auth/sessionStore";
import { PatientClientName } from "@components/PatientClientName";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, useDialog } from "@scribenote/brian-design-system";
import {
  boardChrome,
  ClipboardClip,
  ClipboardCloseButton,
  sheetSurface,
} from "@scribenote/brian-design-system/internal/clipboardChrome";
import { kindLabel, type Subject } from "@subjects/subjectsClient";
import { useSubjectSearch } from "@subjects/subjectsQueries";
import { formatDate } from "@utils/format-date";
import { useDebouncedValue } from "@utils/use-debounced-value";
import { viewCaption, viewMatchesQuery } from "@views/viewIdentity";
import type { View } from "@views/viewsClient";
import { useClinicViews } from "@views/viewsQueries";
import type { TodayVisit } from "@visits/visitsClient";
import { visitsKeys } from "@visits/visitsQueries";
import { useOpenRoom } from "@workspace/useOpenRoom";
import { useWorkspaceNavigation } from "@workspace/useWorkspaceNavigation";
import { useEffect, useRef, useState } from "react";

const MIN_QUERY_LENGTH = 2;
const MAX_VIEW_RESULTS = 5;

/*
 * The palette is the same clipboard object every other modal rides, with two
 * things it does not share: no title bar - what you are looking at is what you
 * just typed, so the search field is the sheet's first line - and it is handed
 * to you high on the desk rather than centred. That is what the raw `useDialog`
 * hook buys: native modal behaviour (top layer, Escape, backdrop dismiss,
 * scroll lock) without the kit Clipboard's header. `mt-[12vh]` with `mb-auto`
 * is what makes an over-constrained abspos box sit high instead of centred;
 * `open:flex` (never a bare `flex`) keeps the UA's
 * `dialog:not([open]) { display: none }` intact.
 *
 * The dialog is transparent and sized to the board it holds: the board is the
 * visible object, and being the `firstElementChild` it also satisfies
 * useDialog's backdrop hit-test, so a click on the pressboard counts as content
 * rather than a dismiss.
 */
const CLIP_SCALE = "md" as const;

const paletteSurface = cn(
  /* max-w-xl is the kit md Clipboard's width, so the palette is that object at
     that size rather than a one-off; the board's margins take the ~1rem the
     paper loses. */
  "mx-auto mt-[12vh] mb-auto w-[calc(100vw-2rem)] max-w-xl flex-col p-0 open:flex",
  /* whitespace-normal, text-left and outline-none for the same reasons the
     kit Clipboard carries them: inherited text properties follow the dialog
     into the top layer from wherever it is mounted, and useDialog parks the
     initial focus on the dialog element itself, which is not a Tab stop and
     needs no ring. */
  "whitespace-normal text-left bg-transparent font-body text-ink outline-none",
  /* Handed to you: the same entrance as the kit's md Clipboard (rises with a
     slight tilt that settles level), so the palette lands like every other
     clipboard. Kept in step with Clipboard's `motionBySize.md` by hand - the
     kit does not export its motion recipe. */
  "transition-[opacity,translate,rotate,overlay,display] transition-discrete duration-250 ease-paper",
  "translate-y-6 rotate-[1.25deg] opacity-0",
  "starting:open:translate-y-6 starting:open:rotate-[1.25deg] starting:open:opacity-0",
  "open:translate-y-0 open:rotate-0 open:opacity-100",
  "motion-reduce:transition-none",
  "backdrop:bg-desk/50 backdrop:backdrop-blur-[1px]",
  "backdrop:transition-[opacity,display] backdrop:transition-discrete backdrop:duration-250 backdrop:ease-paper",
  "backdrop:opacity-0 open:backdrop:opacity-100 starting:open:backdrop:opacity-0",
  "motion-reduce:backdrop:transition-none",
);

type SearchResult =
  | { type: "subject"; subject: Subject }
  | { type: "view"; view: View };

const resultKey = (result: SearchResult) =>
  result.type === "subject"
    ? `subject-${result.subject.kind}-${result.subject.id}`
    : `view-${result.view.id}`;

export const SubjectSearchModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { selectVisit, openSubject, setViewedView } = useWorkspaceNavigation();
  const openRoom = useOpenRoom();
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const { dialogRef, handleCancel, handleClose } = useDialog({
    open: isOpen,
    onClose,
  });

  // A native <dialog> stays mounted whether it is open or not, so nothing here
  // unmounts between openings: reset the term each time it opens, otherwise a
  // prior search and its cached results flash back on reopen.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
    }
  }

  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, 250);
  // Gate on the live query too: `debounced` lags by 250ms, so right after a
  // reset (e.g. reopening the modal) it still holds the old term and would
  // briefly drive a stale, cached result list.
  const ready =
    trimmed.length >= MIN_QUERY_LENGTH && debounced.length >= MIN_QUERY_LENGTH;
  const term = ready ? debounced : "";
  const { data, isFetching } = useSubjectSearch(term);
  const subjects = data ?? [];

  // Views have no text-search endpoint, so filter the clinic's view list
  // locally. Fetched only while the palette is open.
  //
  // Matching is over the title, the subtitle AND the subject names (BE-2395):
  // titles are the kind of document now, so a name-only match would return
  // nothing for "jerry" — the very search people actually type.
  const { data: allViews } = useClinicViews(sessionKey, isOpen);
  const matchedViews =
    ready && allViews
      ? allViews
          .filter((v) => viewMatchesQuery(v, debounced))
          .slice(0, MAX_VIEW_RESULTS)
      : [];

  const results: SearchResult[] = [
    ...subjects.map((subject) => ({ type: "subject", subject }) as const),
    ...matchedViews.map((view) => ({ type: "view", view }) as const),
  ];

  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const open = (result: SearchResult) => {
    if (result.type === "view") {
      setViewedView(result.view.id);
    } else {
      const { subject } = result;
      if (subject.kind === "room") {
        // A room opens the patient in it (see useOpenRoom). Clear the overlays
        // first: a room sits underneath them, and re-opening the same room is a
        // no-op that wouldn't clear them itself.
        setViewedView(null);
        openRoom(subject.id);
      } else {
        // A patient with a visit today opens as a visit selection (aligns the
        // room, carries the visit into the pane header) rather than a bare
        // overlay; everything else (non-patients, patients with no visit today)
        // opens as an overlay. Today's visits are already loaded by the
        // workspace, so read them from the cache without subscribing; if absent
        // we simply fall back to the overlay.
        const visit =
          subject.kind === "patient"
            ? (queryClient
                .getQueryData<TodayVisit[]>(visitsKeys.today(sessionKey))
                ?.find((v) => v.patientId === subject.id) ?? null)
            : null;
        if (visit) selectVisit(visit.id, visit.currentRoomUuid);
        else openSubject(subject);
      }
    }
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const result = results[activeIndex];
      if (result) open(result);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      aria-label="Search"
      onCancel={handleCancel}
      onClose={handleClose}
      data-testid={isOpen ? "subject-search-modal" : undefined}
      className={paletteSurface}
    >
      {/* The board. Its top padding (boardChrome) is the pressboard strip the
          clip lives on; the sheet hangs from under the clip's jaw. The board
          itself never scrolls - only the results list inside the sheet does. */}
      <div
        className={cn("flex min-h-0 min-w-0 flex-col", boardChrome(CLIP_SCALE))}
      >
        {/* The sheet: the paper the search is written on. */}
        <div className={cn("flex min-h-0 min-w-0 flex-col", sheetSurface)}>
          {/* The ruled top line: the field is the paper's first line, so it
              carries the rule under it rather than a border of its own. The
              extra top padding is the clearance the clip's jaw bites into. */}
          <div className="flex items-center gap-3 border-b border-rule px-5 pb-3 pt-6">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              aria-hidden="true"
              className="shrink-0 text-faint"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
              aria-label="Search patients, clients, rooms and views"
              placeholder="Search patients, clients, rooms, views"
              data-testid="subject-search-input"
              // No focus ring on the field: it holds focus for the whole life of
              // the palette, and the palette itself is the object that has been
              // handed to you.
              className={cn(
                "min-w-0 flex-1 bg-transparent text-sm text-ink outline-none",
                "placeholder:font-mono placeholder:text-faint",
              )}
            />
          </div>

          {/* The only scroller in the object: min-w-0 keeps a long name
              truncating instead of widening the sheet, and rounded-b-paper
              stops a scrolled row painting over the paper's bottom corners. */}
          <ul
            role="listbox"
            aria-label="Search results"
            className="flex max-h-[50vh] min-h-0 min-w-0 flex-col overflow-y-auto rounded-b-paper p-2"
          >
            {results.length === 0 ? (
              <li className="px-2 py-6 text-center text-sm text-annotation">
                {trimmed.length < MIN_QUERY_LENGTH
                  ? "Give me at least two letters."
                  : isFetching || !ready
                    ? "Looking."
                    : "Nothing by that name."}
              </li>
            ) : (
              results.map((result, index) => {
                const active = index === activeIndex;
                return (
                  <li key={resultKey(result)} className="min-w-0">
                    <button
                      ref={active ? activeRef : null}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => open(result)}
                      onMouseMove={() => setActiveIndex(index)}
                      className={cn(
                        "flex w-full cursor-pointer items-center justify-between gap-3 rounded-paper px-3 py-2 text-left",
                        "transition-colors duration-150 ease-paper motion-reduce:transition-none",
                        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                        active ? "bg-recess text-ink" : "text-ink-body",
                      )}
                      data-testid={
                        result.type === "subject"
                          ? `subject-search-result-${result.subject.id}`
                          : `view-search-result-${result.view.id}`
                      }
                    >
                      {result.type === "subject" ? (
                        <ResultRow
                          name={result.subject.name}
                          clientName={result.subject.clientName}
                          meta={
                            result.subject.createdAt
                              ? formatDate(result.subject.createdAt)
                              : undefined
                          }
                          kind={kindLabel(result.subject)}
                          kindTestId={`subject-search-kind-${result.subject.kind}`}
                        />
                      ) : (
                        <ResultRow
                          name={result.view.name}
                          meta={[
                            viewCaption(result.view, { withSubject: true }),
                            formatDate(result.view.updatedAt),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          kind="View"
                          kindTestId="view-search-kind"
                        />
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>

        <ClipboardClip scale={CLIP_SCALE} />

        {/* The same bare X every clipboard wears, on the board's strip. The
            palette dismisses on Escape and backdrop too; this is the visible,
            clickable way out. */}
        <ClipboardCloseButton scale={CLIP_SCALE} onClose={onClose} />
      </div>
    </dialog>
  );
};

const ResultRow = ({
  name,
  clientName,
  meta,
  kind,
  kindTestId,
}: {
  name: string;
  /** The owner's name, present on patient results with a linked client. */
  clientName?: string;
  meta?: string;
  /** What kind of thing this is, written as a mono annotation at the end. */
  kind: string;
  kindTestId: string;
}) => (
  <>
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-medium">
        <PatientClientName
          patientName={name}
          clientName={clientName}
          clientClassName="font-normal text-annotation"
        />
      </span>
      {meta && <span className="truncate text-xs text-annotation">{meta}</span>}
    </span>
    <span
      data-testid={kindTestId}
      className="shrink-0 text-annotation-caps text-annotation"
    >
      {kind}
    </span>
  </>
);
